import React, { useEffect, useState } from 'react';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import BbPageHeader from 'shared/components/BbPageHeader';
import PartnerGhostBanner from 'shared/components/PartnerGhostBanner';
import { isGhostingAsBb } from 'shared/utils/partnerUser';
import { fetchPartnerGhostLogin } from 'shared/utils/partnerGhostLogin';
import BbIntro from './BbIntro';
import TasksView from '../CnWidget/TasksView';
import ReportStatusModal from '../CnWidget/ReportStatusModal';
import {
  fetchPartnerLogin,
  type PartnerEntry,
  persistPartnerSession,
  reportBuilderUrl,
  resolvePartnerEntry,
} from '../CnWidget/utils';

const bbLoginError = (status: number) =>
  status === 401
    ? 'We couldn\'t verify your identity. Try again from Blackbaud.'
    : 'Something went wrong. Contact your administrator if the problem continues.';

export default function BbWidget() {
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [entry, setEntry] = useState<PartnerEntry | null>(null);
  // Intro splash gates every state; button defers the resolved action.
  const [entered, setEntered] = useState(false);
  const ghostingAsBb = isGhostingAsBb();

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');

    if (!token && !ghostingAsBb) {
      setError('Your session link is invalid. Try opening it again from Blackbaud.');
      setIsLoading(false);
      return;
    }

    const doLogin = async () => {
      try {
        const data = ghostingAsBb
          ? await fetchPartnerGhostLogin('bb')
          : await fetchPartnerLogin('bb', token ?? '', bbLoginError);
        if (!data?.ti_token) throw new Error('Something went wrong. Try again.');
        if (!ghostingAsBb) persistPartnerSession(data, 'bb');
        setEntry(resolvePartnerEntry(data));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setIsLoading(false);
      }
    };

    doLogin();
  }, []);

  const handleEnter = () => {
    if (!entry) return;
    if (entry.kind === 'redirect') window.location.href = entry.href;
    else setEntered(true);
  };

  return (
    <div className="bb-page">
      {ghostingAsBb && <PartnerGhostBanner partner="bb" error={error} />}

      {isLoading && (
        <div className="bb-loading-container">
          <TrueImpactLoading />
        </div>
      )}

      {!isLoading && !error && !entered && (
        <BbIntro buttonLabel="Enter True Impact" onEnter={handleEnter} />
      )}

      {entered && entry?.kind === 'published' && (
        <ReportStatusModal
          partner="bb"
          title="Report Published"
          description="Your report has been published."
          buttonLabel="View Published Report"
          reportId={entry.reportId}
        />
      )}

      {entered && entry?.kind === 'completed' && (
        <ReportStatusModal
          partner="bb"
          title="Report Completed"
          description="Your Impact Report has been completed. Thank you!"
          buttonLabel="View Report"
          reportId={entry.reportId}
        />
      )}

      {entered && entry?.kind === 'tasks' && (
        <>
          <BbPageHeader
            title="Pending Tasks"
            subtitle="Review and complete the reporting tasks that require your attention."
          />
          <TasksView
            tasks={entry.tasks}
            onViewReport={reportId => {
              window.location.href = reportBuilderUrl(reportId);
            }}
          />
        </>
      )}

      {!isLoading && error && (
        <div className="bb-error-container">
          <div className="bb-error-box">
            <p className="bb-error-title">Unable to sign in</p>
            <p className="bb-error-message">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
}
